import FancyClothesContext from "../../website/components/FancyClothesContext";
import { useContext, useMemo } from "react";

type DesignProviderValues = {
  backgroundColor: string;
  fontColor: string;
  fontFamily: string;
  fontSizeFactor: number;
};

type ButtonValues = {
  buttonColor: string;
  cornerStyle: string;
  fontFamily: string;
  fontSizeFactor: number;
  lineHeightFactor: number;
};

type HeadlineValues = {
  fontColor: string;
  fontFamily: string;
  fontSizeFactor: number;
  lineHeightFactor: number;
};

type FancyClothesDesignValues = {
  button: ButtonValues;
  designProvider: DesignProviderValues;
  smallHeadline: HeadlineValues;
};

/**
 * Custom hook to extract design values from FancyClothesContext.
 * Provides access to DesignProvider and Button scope values.
 */
export const useFancyClothesDesign = (): FancyClothesDesignValues => {
  const fancyClothesData = useContext(FancyClothesContext) as any;

  return useMemo(() => {
    const { colorway, design } = fancyClothesData || {};

    // DesignProvider scope values
    const designProviderScope = design?.DesignProvider;
    const designProviderColorway =
      designProviderScope?.scopes?.[`Colorway${colorway}`];

    const designProvider: DesignProviderValues = {
      backgroundColor:
        designProviderColorway?.rules?.backgroundColor || "#ffffff",
      fontColor: designProviderColorway?.rules?.fontColor || "#000000",
      fontFamily: designProviderScope?.rules?.fontFamily || "inherit",
      fontSizeFactor: designProviderScope?.rules?.fontSizeFactor || 1,
    };

    // Button scope values
    const buttonScope = design?.Button;
    const buttonColorway = buttonScope?.scopes?.[`Colorway${colorway}`];

    const button: ButtonValues = {
      buttonColor: buttonColorway?.rules?.buttonColor || "#000000",
      cornerStyle: buttonScope?.rules?.cornerStyle || "square",
      fontFamily: buttonScope?.rules?.fontFamily || "inherit",
      fontSizeFactor: buttonScope?.rules?.fontSizeFactor || 1,
      lineHeightFactor: buttonScope?.rules?.lineHeightFactor || 1.6,
    };

    // SmallHeadline scope values
    const smallHeadlineScope = design?.SmallHeadline;
    const smallHeadlineColorway =
      smallHeadlineScope?.scopes?.[`Colorway${colorway}`];

    const smallHeadline: HeadlineValues = {
      fontColor:
        smallHeadlineColorway?.rules?.fontColor || designProvider.fontColor,
      fontFamily: smallHeadlineScope?.rules?.fontFamily || "inherit",
      fontSizeFactor: smallHeadlineScope?.rules?.fontSizeFactor || 1,
      lineHeightFactor: smallHeadlineScope?.rules?.lineHeightFactor || 1.2,
    };

    return {
      button,
      designProvider,
      smallHeadline,
    };
  }, [fancyClothesData]);
};
